import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';

// OPTION 2: Scrolling Text Reveal with Image Background
export const ScrollingTextReveal = () => {
  const sectionRef = useRef(null);
  
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"]
  });

  const textX1 = useTransform(scrollYProgress, [0, 1], ['-20%', '20%']);
  const textX2 = useTransform(scrollYProgress, [0, 1], ['20%', '-20%']);

  return (
    <section 
      ref={sectionRef}
      className="relative bg-[#0B0B0C] overflow-hidden py-10 md:py-14 lg:py-16"
    >
      {/* Image background - more visible */}
      <div 
        className="absolute inset-0 bg-cover bg-center opacity-35"
        style={{
          backgroundImage: `url('https://customer-assets.emergentagent.com/job_b7b64ad3-38ad-44dd-8a38-e1acc295afe8/artifacts/yl4pvsla_IMG_2699.jpeg')`,
        }}
      />
      
      {/* Overlay gradient - lighter to show more image */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#0B0B0C]/80 via-transparent to-[#0B0B0C]/80" />

      {/* Scrolling text layers with center content — relative flex so content dictates height (no clipping in Safari, mobile Safari, or any browser) */}
      <div className="relative z-10 flex flex-col items-center justify-center gap-4 md:gap-6 lg:gap-8">
        {/* Top scrolling text */}
        <motion.div 
          className="w-full whitespace-nowrap overflow-hidden shrink-0"
          style={{ x: textX1 }}
        >
          <span className="font-display text-[3vw] md:text-[3.5vw] text-white/35 uppercase tracking-wider">
            ENGINEERED FOR CHAMPIONS • ENGINEERED FOR CHAMPIONS • ENGINEERED FOR CHAMPIONS •
          </span>
        </motion.div>
        
        {/* Center content - looping action videos with baked-in SMASH/SWEAT/REPEAT text */}
        <div className="text-center px-4 flex flex-col items-center">
          {/* Looping action videos — texts normalized to SMASH's exact position via per-video vertical scale + translate (precise OpenCV measurements). */}
          <div
            className="flex gap-2 md:gap-3 p-1.5 md:p-2 rounded-xl border border-white/10 bg-black/40 backdrop-blur-sm shadow-[0_8px_28px_-12px_rgba(0,0,0,0.7)] w-[min(94vw,640px)] md:w-[min(82vw,800px)] lg:w-[min(74vw,960px)]"
            data-testid="action-clips-strip"
          >
            {[
              // Precise OpenCV measurements (text bbox in source 1248×1664):
              //   SMASH : top=134, height=256, center_x=640  (reference — no transform)
              //   SWEAT : top=151, height=282, center_x=658  → sy = 256/282 = 0.908; ty = -(151×0.908-134)/1664 = -0.19%
              //   REPEAT: top=224, height=271, center_x=643  → sy = 256/271 = 0.945; ty = -(224×0.945-134)/1664 = -4.67%
              // scaleY only (not uniform) keeps full width visible so no horizontal letter clipping; 5-9% vertical squish is barely noticeable on motion.
              // Self-hosted, compressed for INSTANT loading (480px, ~350KB each; originals were 9-12MB on an external CDN).
              { mp4: '/clips/smash.mp4', webm: '/clips/smash.webm', poster: '/clips/smash-poster.jpg', label: 'Smash', sy: 1.000, ty: 0.00 },
              { mp4: '/clips/sweat.mp4', webm: '/clips/sweat.webm', poster: '/clips/sweat-poster.jpg', label: 'Sweat', sy: 0.908, ty: -0.19 },
              { mp4: '/clips/repeat.mp4', webm: '/clips/repeat.webm', poster: '/clips/repeat-poster.jpg', label: 'Repeat', sy: 0.945, ty: -4.67 },
            ].map((clip) => (
              <div
                key={clip.label}
                className="relative flex-1 min-w-0 aspect-[3/4] rounded-lg overflow-hidden bg-black"
              >
                <video
                  poster={clip.poster}
                  autoPlay
                  loop
                  muted
                  playsInline
                  preload="auto"
                  aria-label={clip.label}
                  className="absolute inset-0 w-full h-full object-cover"
                  style={{
                    transformOrigin: 'top center',
                    transform: `translateY(${clip.ty}%) scaleY(${clip.sy})`,
                  }}
                >
                  <source src={clip.mp4} type="video/mp4" />
                  <source src={clip.webm} type="video/webm" />
                </video>
                {/* Bottom mask — hides DYSSC GAMECORE watermark and the small gap left by vertical scale-down */}
                <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-[50%] bg-gradient-to-t from-black via-black to-transparent" />
              </div>
            ))}
          </div>
        </div>

        {/* Bottom scrolling text */}
        <motion.div 
          className="w-full whitespace-nowrap overflow-hidden shrink-0"
          style={{ x: textX2 }}
        >
          <span className="font-display text-[3vw] md:text-[3.5vw] text-[#E02128]/45 uppercase tracking-wider">
            PRECISION • POWER • CONTROL • PRECISION • POWER • CONTROL • PRECISION •
          </span>
        </motion.div>
      </div>
    </section>
  );
};
